import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Alert, Collapse, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { btoa } from 'js-base64';
import { toast } from 'react-toastify';
import api from '../../services/api';
import moment from 'moment';


// 在生成連結時，確保轉換為字串再編碼
const generateEncodedId = (id) => {
  return btoa(String(id));
};

// 在文件頂部添加新的樣式
const pulsingBadgeStyle = {
  animation: 'pulseBadge 2s infinite',
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.35em 0.65em',
  borderRadius: '50rem'
};

// 在 return 語句之前添加樣式定義
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulseBadge {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;
document.head.appendChild(styleSheet);

const getTypeStyle = (type) => {
  const styles = {
    SKI: { 
      backgroundColor: 'rgba(232, 245, 233, 0.4)',  // 淺綠色背景，增加透明度
      color: '#1b5e20',  // 深綠色文字
      border: '1px solid #1b5e20'  // 添加深綠色邊框
    },
    SNOWBOARD: { 
      backgroundColor: 'rgba(255, 243, 224, 0.4)',  // 淺橘色背景，增加透明度
      color: '#e65100',  // 深橘色文字
      border: '1px solid #e65100'  // 添加深橘色邊框
    }
  };

  const commonTypeStyle = {
    padding: '0.35rem 0.8rem',  // 稍微增加內邊距
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '600',  // 增加字體粗細
    display: 'inline-block',  // 確保邊框完整顯示
    textTransform: 'uppercase',  // 文字大寫，增加視覺重要性
    letterSpacing: '0.5px'  // 增加字母間距
  };

  const typeStyle = {
    marginLeft: '0.5rem',
  };

  const mobileTypeStyle = {
    marginLeft: '0rem',
    marginBottom: '0.5rem'
  };

  const isMobile = window.innerWidth < 768;

  return { 
    ...styles[type],
    ...commonTypeStyle,
    ...(isMobile ? mobileTypeStyle : typeStyle)
  };
};

function CoachDashboard() {
  const [coachInfo, setCoachInfo] = useState(null);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const navigate = useNavigate();
  const upcomingBookingsLimit = 10;
  const collapseLimit = 4;

  const coachId = JSON.parse(localStorage.getItem('user')).id;
  const [scheduleLink, setScheduleLink] = useState(`${window.location.origin}/public/coach/${generateEncodedId(coachId)}/schedule`);

  const [latestScheduleDate, setLatestScheduleDate] = useState(null);

  // 獲取教練資訊
  useEffect(() => {
    const fetchCoachInfo = async () => {
      try {
        const response = await api.get(`/coaches/${coachId}/`);
        setCoachInfo(response.data);
      } catch (error) {
        console.error('Error fetching coach info:', error);
      }
    };

    if (coachId) {
      fetchCoachInfo();
    }
  }, [coachId]);

  // 獲取即將到來的預約
  useEffect(() => {
    const fetchUpcomingBookings = async () => {
      try {
        const response = await api.get('/bookings/upcoming/', {
          params: {
            limit: upcomingBookingsLimit
          }
        });
        response.data.sort((a, b) => {
          // 先比較日期
          const dateComparison = moment(a.date).diff(moment(b.date));
          if (dateComparison !== 0) return dateComparison;
          
          // 日期相同時比較時段
          return a.time_slot === 'MORNING' ? -1 : 1;
        });
        setUpcomingBookings(response.data);
      } catch (error) {
        console.error('Error fetching upcoming bookings:', error);
        toast.error('獲取預約資訊失敗');
      }
    };

    if (coachId) {
      fetchUpcomingBookings();
    }
  }, [coachId]);

  // 獲取待處理的預約
  useEffect(() => {
    const fetchPendingBookings = async () => {
      try {
        const response = await api.get('/bookings/pending/');
        setPendingBookings(response.data);
      } catch (error) {
        console.error('Error fetching pending bookings:', error);
        toast.error('獲取待處理預約資訊失敗');
      }
    };

    if (coachId) {
      fetchPendingBookings();
    }
  }, [coachId]);

  useEffect(() => {
    // 獲取即將到來的行程
    const fetchUpcomingSchedules = async () => {
      try {
        const response = await api.get('/schedules/upcoming/', {
          params: {
            limit: 1
          }
        });
        setLatestScheduleDate(response.data[0].date);
      } catch (error) {
        console.error('Error fetching upcoming schedules:', error);
      }
    };

    if (coachId) {
      fetchUpcomingSchedules();
    }
  }, [coachId]);

  useEffect(() => {
    if (latestScheduleDate) {
      let year = latestScheduleDate.split('-')[0];
      let month = latestScheduleDate.split('-')[1];
      setScheduleLink(`${window.location.origin}/public/coach/${generateEncodedId(coachId)}/schedule/${year}-${month}`);
    }
  }, [latestScheduleDate]);

  const handleShowDetails = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  return (
    <div>
      <h2 className="mb-4">{coachInfo ? `${coachInfo.username} 教練，您好！` : '教練儀表板'}</h2>
      
      {/* 課程輔助按鈕 */}
      <Row className="mb-3">
        <Col md={12}>
          <div className="d-flex justify-content-start">
            <Button 
              variant="outline-success"
              size="lg"
              onClick={() => navigate('/coach/assistant')}
              className="d-flex align-items-center"
            >
              🎿 課程輔助工具
            </Button>
          </div>
        </Col>
      </Row>

      {/* 顯示即將到來的預約 */}
      {upcomingBookings.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Body>
                <Card.Title className="d-flex justify-content-between align-items-center">
                  <span>即將到來的{upcomingBookingsLimit}堂課</span>
                  {upcomingBookings.length > collapseLimit && (
                    <Button 
                      variant="link"
                      onClick={() => setShowAllBookings(!showAllBookings)}
                      className="text-decoration-none"
                    >
                      {showAllBookings ? '收起' : '查看全部'}
                    </Button>
                  )}
                </Card.Title>

                {/* 永遠顯示前collapseLimit個預約 */}
                {upcomingBookings.slice(0, collapseLimit).map((booking) => (
                  <Alert 
                    key={booking.id} 
                    variant="info" 
                    className="d-flex justify-content-between align-items-center mb-2"
                  >
                    <div>
                      <div className="d-flex flex-column flex-md-row mb-1 mb-md-0">
                        <div className="ms-0 ms-md-3 order-first order-md-last">
                          <span style={getTypeStyle(booking.type)}>{booking.type_display}</span>
                        </div>
                        <div className="d-flex flex-row mb-1 mb-md-0">
                          <div>
                            <strong>地點：</strong>{booking.preferred_location}
                          </div>
                          <div className="ms-4">
                            <strong>學生：</strong>{booking.student_name}
                          </div>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-1 mb-md-0">
                        <div>
                          上課時間：{moment(booking.date).format('YYYY/MM/DD')}
                        </div>
                        <span 
                          className="ms-2 px-2 py-1 rounded-2"
                          style={{ 
                            backgroundColor: '#fff3cd',
                            color: '#997404',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}
                        >
                          {booking.time_slot === 'MORNING' ? '早上' : '下午'}
                        </span>
                      </div>
                    </div>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleShowDetails(booking)}
                    >
                      詳情
                    </Button>
                  </Alert>
                ))}

                {/* 使用 Collapse 顯示剩餘的預約 */}
                {upcomingBookings.length > collapseLimit && (
                  <Collapse in={showAllBookings}>
                    <div>
                      {upcomingBookings.slice(collapseLimit).map((booking) => (
                        <Alert 
                          key={booking.id} 
                          variant="info" 
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <div>
                            <div className="d-flex flex-column flex-md-row mb-1 mb-md-0">
                              <div className="ms-0 ms-md-3 order-first order-md-last">
                                <span style={getTypeStyle(booking.type)}>{booking.type_display}</span>
                              </div>
                              <div className="d-flex flex-row mb-1 mb-md-0">
                                <div>
                                  <strong>地點：</strong>{booking.preferred_location}
                                </div>
                                <div className="ms-4">
                                  <strong>學生：</strong>{booking.student_name}
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex align-items-center mb-1 mb-md-0">
                              <div>
                                上課時間：{moment(booking.date).format('YYYY/MM/DD')}
                              </div>
                              <span 
                                className="ms-2 px-2 py-1 rounded-2"
                                style={{ 
                                  backgroundColor: '#fff3cd',
                                  color: '#997404',
                                  fontSize: '0.875rem',
                                  fontWeight: '500'
                                }}
                              >
                                {booking.time_slot === 'MORNING' ? '早上' : '下午'}
                              </span>
                            </div>
                          </div>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => handleShowDetails(booking)}
                          >
                            詳情
                          </Button>
                        </Alert>
                      ))}
                    </div>
                  </Collapse>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>文章管理</Card.Title>
              <Card.Text>
                管理您的文章
              </Card.Text>
              <Button 
                variant="primary"
                onClick={() => navigate('/articles/manage')}
              >
                前往文章
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>行程管理</Card.Title>
              <Card.Text>
                管理您的行程表
              </Card.Text>
              <Button 
                variant="primary"
                onClick={() => navigate('/coach/schedule')}
              >
                前往行程表
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
      <Col md={4}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>
                預約管理
                {pendingBookings.length > 0 && (
                  <span 
                    style={pulsingBadgeStyle}
                    className="badge ms-4"
                  >
                    <span 
                      style={{
                        background: 'linear-gradient(45deg, #ff6b6b, #ff8787)',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '50rem',
                        boxShadow: '0 2px 4px rgba(255, 107, 107, 0.4)',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                    >
                      {pendingBookings.length} 個待處理
                    </span>
                  </span>
                )}
              </Card.Title>
              <Card.Text>
                查看和管理學員預約
              </Card.Text>
              <Button 
                variant="primary"
                onClick={() => navigate('/coach/booking')}
              >
                前往預約
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>學員評量</Card.Title>
              <Card.Text>
                評量學員的學習成果
              </Card.Text>
              <Button 
                variant="primary"
                onClick={() => navigate('/coach/evaluation')}
              >
                前往評量
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={8}>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>行程表連結</Card.Title>
              <Card.Text>
                複製您的行程表連結分享給學員
              </Card.Text>
              <Card.Text>
                <a href={scheduleLink} target="_blank" rel="noopener noreferrer">
                  {scheduleLink}
                </a>
              </Card.Text>
              <Button
                variant="primary" 
                onClick={() => {
                  navigator.clipboard.writeText(scheduleLink);
                  toast.success(`連結已複製到剪貼簿!`, { autoClose: 1000, position: "top-center" });
                }}
              >
                複製連結
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>課程詳情</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              <p><strong>學生：</strong>{selectedBooking.student_name}</p>
              <p><strong>課程類型：</strong>{selectedBooking.type_display}</p>
              <p><strong>學生人數：</strong>{selectedBooking.numberof_students}</p>
              <p><strong>上課地點：</strong>{selectedBooking.preferred_location}</p>
              <p>
                <strong>上課時間：</strong>
                {moment(selectedBooking.date).format('YYYY/MM/DD')}
                <span 
                  className="ms-2 px-2 py-1 rounded-2"
                  style={{ 
                    backgroundColor: '#fff3cd',
                    color: '#997404',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {selectedBooking.time_slot === 'MORNING' ? '早上' : '下午'}
                </span>
              </p>
              {selectedBooking.note && (
                <p><strong>備註：</strong>{selectedBooking.note}</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CoachDashboard; 